import { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import WhiteMainBackground from "../../../shared_components/WhiteMainBackground/WhiteMainBackground";
import MainHeading from "../../../shared_components/MainHeading/MainHeading";
import QaProcessDetailStyle from "./QaProcessDetail.module.css";
import { Accordion } from "react-bootstrap";
import AccordionItem from "react-bootstrap/esm/AccordionItem";
import AccordionHeader from "react-bootstrap/esm/AccordionHeader";
import AccordionBody from "react-bootstrap/esm/AccordionBody";
import Spinner from "../../../shared_components/Spinner/Spinner";
import InfoPage from "../../InfoPage/InfoPage";
import UserData from "../../../services/UserData";
import QaProcessService from "../../../services/QaProcessService";

const STAGE_FLOW = ["Briefs", "Audio", "Design", "Coding"];

const getStageKeyword = (stage) => {
  const normalized = String(stage || "").trim().toLowerCase();
  if (normalized === "briefs") {
    return "brief";
  }
  return normalized;
};

const getChecklistStage = (title) => {
  const normalizedTitle = String(title || "").trim().toLowerCase();
  const matchedStage = STAGE_FLOW.find((stage) =>
    normalizedTitle.startsWith(getStageKeyword(stage))
  );
  return matchedStage || "";
};

const QaProcessDetail = () => {
  const { assetKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userData = useContext(UserData);

  const referrer = location.state?.referrer || "/qa-process";
  const asset = location.state?.asset || null;
  const resumeStage = location.state?.resumeStage || "";

  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeKey, setActiveKey] = useState(null);
  const [saveStatus, setSaveStatus] = useState("Saved");

  useEffect(() => {
    if (assetKey) {
      QaProcessService.recordQaAssetLastAccessed(assetKey);
    }
  }, [assetKey]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorMessage("");

    QaProcessService.getAssetChecklists(userData, assetKey)
      .then((result) => {
        if (!cancelled) {
          setChecklists(result);
          const currentStage = QaProcessService.deriveQaStageFromChecklists(result);
          QaProcessService.recordQaAssetStage(assetKey, currentStage);

          const targetStage = resumeStage && resumeStage !== "Completed"
            ? resumeStage
            : currentStage;
          const stageChecklist = result.find(
            (checklist) => getChecklistStage(checklist.title) === targetStage
          );
          if (stageChecklist?.id) {
            setActiveKey(String(stageChecklist.id));
          } else if (result[0]?.id) {
            setActiveKey(String(result[0].id));
          }
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setErrorMessage(error?.message || "Unable to load checklists.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userData, assetKey, resumeStage]);

  const backButtonAction = () => {
    navigate(referrer);
  };

  const handleCheckboxChange = async (checklistId, itemId) => {
    const currentChecklist = checklists.find((checklist) => checklist.id === checklistId);
    const currentItem = currentChecklist?.items.find((item) => item.id === itemId);
    if (!currentItem) {
      return;
    }

    const nextChecked = !currentItem.checked;
    setSaveStatus("Saving...");
    const nextChecklists = checklists.map((checklist) =>
        checklist.id === checklistId
          ? {
            ...checklist,
            items: checklist.items.map((item) =>
              item.id === itemId ? { ...item, checked: nextChecked } : item
            ),
          }
          : checklist
    );
    setChecklists(nextChecklists);

    try {
      await QaProcessService.updateChecklistItem(
        userData,
        checklistId,
        itemId,
        nextChecked
      );
      const currentStage = QaProcessService.deriveQaStageFromChecklists(nextChecklists);
      QaProcessService.recordQaAssetStage(assetKey, currentStage);
      setSaveStatus("Saved");
    } catch (error) {
      const revertedChecklists = nextChecklists.map((checklist) =>
          checklist.id === checklistId
            ? {
              ...checklist,
              items: checklist.items.map((item) =>
                item.id === itemId ? { ...item, checked: !nextChecked } : item
              ),
            }
            : checklist
      );
      setChecklists(revertedChecklists);
      const currentStage = QaProcessService.deriveQaStageFromChecklists(revertedChecklists);
      QaProcessService.recordQaAssetStage(assetKey, currentStage);
      setSaveStatus("Save failed");
      setErrorMessage(error?.message || "Unable to update checklist item.");
    }
  };

  const calculateCategoryProgress = (items) => {
    const checked = items.filter((item) => item.checked).length;
    return items.length > 0 ? Math.round((checked / items.length) * 100) : 0;
  };

  const currentStage = QaProcessService.deriveQaStageFromChecklists(checklists);
  const getStageCounts = (stage) => {
    const stageChecklist = checklists.find(
      (checklist) => getChecklistStage(checklist.title) === stage
    );
    const items = Array.isArray(stageChecklist?.items) ? stageChecklist.items : [];
    const checked = items.filter((item) => Boolean(item?.checked)).length;
    return { checked, total: items.length };
  };
  const activeStage = currentStage === "Completed" ? "Coding" : currentStage;
  const stageCounts = getStageCounts(activeStage);
  const progressStatus = currentStage === "Completed"
    ? "Completed"
    : `${currentStage} (${stageCounts.checked}/${stageCounts.total})`;

  const navigateToStage = (stage) => {
    const stageChecklist = checklists.find(
      (checklist) => getChecklistStage(checklist.title) === stage
    );
    if (!stageChecklist?.id) {
      return;
    }
    setActiveKey(String(stageChecklist.id));
    const targetElement = document.getElementById(`qa-section-${stageChecklist.id}`);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const renderChecklistCategory = (checklist) => {
    const { id, title, description, items } = checklist;
    const progress = calculateCategoryProgress(items);

    return (
      <AccordionItem eventKey={id} key={id}>
        <div id={`qa-section-${id}`} className={QaProcessDetailStyle.anchor_target} />
        <AccordionHeader>
          <div className={QaProcessDetailStyle.accordion_header_content}>
            <div className={QaProcessDetailStyle.accordion_header_left}>
              <div className={QaProcessDetailStyle.category_info}>
                <h3 className={QaProcessDetailStyle.category_title}>{title}</h3>
                {description && (
                  <p className={QaProcessDetailStyle.category_description}>
                    {description}
                  </p>
                )}
              </div>
            </div>
            <div className={QaProcessDetailStyle.accordion_header_right}>
              <span className={QaProcessDetailStyle.progress_text}>
                {progress}% Done
              </span>
              <div className={QaProcessDetailStyle.progress_bar_container}>
                <div
                  className={QaProcessDetailStyle.progress_bar}
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          </div>
        </AccordionHeader>
        <AccordionBody>
          <div className={QaProcessDetailStyle.category_items}>
            {items.map((item, index) => {
              return (
                <div
                  key={`${id}-${item.id}-${index}`}
                  className={QaProcessDetailStyle.checklist_item}
                  onClick={() => handleCheckboxChange(id, item.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleCheckboxChange(id, item.id);
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(item.checked)}
                    onChange={() => handleCheckboxChange(id, item.id)}
                    onClick={(e) => e.stopPropagation()}
                    className={QaProcessDetailStyle.item_checkbox}
                  />
                  <span
                    className={`${QaProcessDetailStyle.item_text} ${item.checked ? QaProcessDetailStyle.item_text_checked : ""
                      }`}
                  >
                    {item.text}
                  </span>
                </div>
              );
            })}
          </div>
        </AccordionBody>
      </AccordionItem>
    );
  };

  const assetName = asset?.name || assetKey || "QA Process";

  if (loading) {
    return (
      <WhiteMainBackground>
        <Spinner />
      </WhiteMainBackground>
    );
  }

  if (errorMessage && checklists.length === 0) {
    return (
      <WhiteMainBackground>
        <InfoPage reason="Unable to load QA checklist" description={errorMessage} />
      </WhiteMainBackground>
    );
  }

  return (
    <WhiteMainBackground>
      <div className={QaProcessDetailStyle.main}>
        <MainHeading backButtonAction={backButtonAction}>
          QA CHECKLIST: {assetName.toUpperCase()}
        </MainHeading>

        <div className={QaProcessDetailStyle.status_bar}>
          <span className={QaProcessDetailStyle.status_text}>
            Status: {progressStatus}
          </span>
          <span className={QaProcessDetailStyle.save_status}>Autosave: {saveStatus}</span>
        </div>
        <div className={QaProcessDetailStyle.stage_chips}>
          {STAGE_FLOW.map((stage) => (
            <button
              key={stage}
              type="button"
              className={`${QaProcessDetailStyle.stage_chip} ${currentStage === stage ? QaProcessDetailStyle.stage_chip_active : ""}`}
              onClick={() => navigateToStage(stage)}
            >
              {stage}
            </button>
          ))}
        </div>
        {errorMessage ? (
          <div className={QaProcessDetailStyle.action_required}>
            <span className={QaProcessDetailStyle.action_icon}>⚠️</span>
            <span className={QaProcessDetailStyle.action_text}>{errorMessage}</span>
          </div>
        ) : null}

        <div className={QaProcessDetailStyle.action_required}>
          <span className={QaProcessDetailStyle.action_icon}>⚠️</span>
          <span className={QaProcessDetailStyle.action_text}>
            Review all sections below and mark each item as complete. Your
            progress is saved automatically.
          </span>
        </div>

        <div className={QaProcessDetailStyle.accordion_container}>
          <Accordion
            activeKey={activeKey}
            onSelect={(eventKey) => {
              if (eventKey == null) {
                setActiveKey(null);
                return;
              }
              setActiveKey((current) =>
                current === String(eventKey) ? null : String(eventKey)
              );
            }}
          >
            {checklists.map((checklist) => renderChecklistCategory(checklist))}
          </Accordion>
        </div>
      </div>
    </WhiteMainBackground>
  );
};

export default QaProcessDetail;
